import { memo, useCallback, useMemo } from "react";
import { styles } from "../form";
import { colors } from "../../utils/const";
import { useFormik } from "formik";
import { EnOffersSchema } from "../../utils/en_schema";
import { ArOffersSchema } from "../../utils/ar_schema";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { useI18n } from "@/i18n/I18nProvider";
import { api } from "../../utils/Axios";

function OfferForm({ product = "shopping", source = null }) {
  const { t, locale } = useI18n();

  const validationSchema = useMemo(
    () => (locale === "en" ? EnOffersSchema : ArOffersSchema),
    [locale],
  );

  const initialValues = useMemo(
    () => ({
      name: "",
      email: "",
      mobilePhone: "",
    }),
    [],
  );

  const buildPayload = useCallback((values) => {
    const phoneWithCode = values.mobilePhone ? `+2${values.mobilePhone}` : "";

    return {
      name: values.name || "",
      mobilePhone: phoneWithCode,
      email: values.email || "",

      POS: "",
      amount: "0",
      area: "",
      brand: "",
      businessIndustry: "",
      businessName: "",
      buyerEmail: "",
      buyerName: "",
      buyerPhone: "",
      carCondition: "",
      category: "",
      commercialTax: "",
      companyName: "",
      downPayment: "0",
      eCommerce: "",
      electricityBill: "",
      governance: "",
      homeAddress: "",
      homePhone: "",
      hrLetter: "",
      installmentValue: "0",
      insuranceType: "",
      mobile: phoneWithCode,
      model: "",
      modelYear: "",
      mortgageType: "",
      needHelp: false,
      noOfBranches: "0",
      noOfEmployees: "0",
      plan: "",
      propertyGovern: "",
      refIdNo: "",
      refIdNumber: "",
      refName: "",
      refPhoneNumber: "",
      refProduct: "",
      siteLink: "",
      tenor: "0",
      crmId: product,
      formSource: source,
    };
  }, []);

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        const payload = buildPayload(values);

        await api.post(`/services/request-info`, payload);

        toast.success(t.formMsg, {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });

        actions.resetForm();
      } catch (error) {
        toast.error(t.formMsgFail, {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } finally {
        actions.setSubmitting(false);
      }
    },
    [buildPayload, t.formMsg, t.formMsgFail],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: false,
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const handlePhoneChange = useCallback(
    (e) => {
      const v = (e.target.value || "").replace(/\D/g, "").slice(0, 11);
      setFieldValue("mobilePhone", v, false);
    },
    [setFieldValue],
  );

  const helper = useCallback(
    (name) => (touched[name] && errors[name] ? errors[name] : "\u00A0"),
    [touched, errors],
  );

  return (
    <Box component="form" autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Box sx={{ ...styles.application_form, bgcolor: "#fff", p: 1 }}>
        {/* Full name */}
        <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
          <InputLabel sx={styles.label}>{t.form_labels.full_name}</InputLabel>
          <TextField
            value={values.name}
            onChange={handleChange}
            id="name"
            name="name"
            type="text"
            onBlur={handleBlur}
            error={Boolean(touched.name && errors.name)}
            sx={{ ...styles.input }}
            InputProps={{ sx: { height: "45px" } }}
            variant="outlined"
          />
          <Box sx={styles.helperText}>{helper("name")}</Box>
        </Box>

        {/* Email */}
        <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
          <InputLabel sx={styles.label}>
            {t.form_labels.email_adress}
          </InputLabel>
          <TextField
            value={values.email}
            onChange={handleChange}
            id="email"
            name="email"
            type="email"
            onBlur={handleBlur}
            error={Boolean(touched.email && errors.email)}
            sx={{ ...styles.input }}
            InputProps={{ sx: { height: "45px" } }}
            variant="outlined"
          />
          <Box sx={styles.helperText}>{helper("email")}</Box>
        </Box>

        {/* Phone */}
        <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
          <InputLabel sx={styles.label}>
            {t.form_labels.phone_number}
          </InputLabel>
          <TextField
            value={values.mobilePhone}
            onChange={handlePhoneChange}
            id="mobilePhone"
            name="mobilePhone"
            type="tel"
            onBlur={handleBlur}
            error={Boolean(touched.mobilePhone && errors.mobilePhone)}
            sx={{ ...styles.input }}
            InputProps={{
              sx: { height: "45px" },
              inputProps: {
                inputMode: "numeric",
                maxLength: 11,
              },
            }}
            variant="outlined"
          />
          <Box sx={styles.helperText}>{helper("mobilePhone")}</Box>
        </Box>

        <Button
          type="submit"
          disabled={isSubmitting}
          aria-label="Offer form submit button"
          sx={{ ...styles.btn }}
          style={{ backgroundColor: colors.orange }}
        >
          {t.form_labels.request_service}
        </Button>
      </Box>
    </Box>
  );
}

export default memo(OfferForm);
